import { useEffect, useState, useMemo } from "react";
import { Plus, CheckCircle2, Circle, Trash2, Pencil, ListTodo, Flame, AlertTriangle } from "lucide-react";
import {
  listTasks, createTask, updateTask, deleteTask, listUsers, formatBRDate,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const PRIORITY_META = {
  high: { label: "Alta", cls: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Média", cls: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  low: { label: "Baixa", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const isOverdue = (task) => {
  if (task.status === "completed" || !task.due_date) return false;
  return task.due_date < new Date().toISOString().slice(0, 10);
};

const emptyForm = {
  title: "", description: "", assignee_id: "", priority: "medium", due_date: "",
};

export default function Tasks() {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [t, u] = await Promise.all([
        listTasks(),
        isAdmin ? listUsers().catch(() => []) : Promise.resolve([]),
      ]);
      setTasks(t);
      setUsers(u);
    } catch {
      toast.error("Erro ao carregar tarefas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(
    () => tasks.filter((t) => (filter === "all" ? true : t.status === filter)),
    [tasks, filter]
  );

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title, description: t.description || "",
      assignee_id: t.assignee_id, priority: t.priority,
      due_date: t.due_date || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assignee_id) {
      toast.error("Preencha título e responsável");
      return;
    }
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editing) await updateTask(editing.id, payload);
      else await createTask(payload);
      toast.success(editing ? "Tarefa atualizada" : "Tarefa criada");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const toggleComplete = async (t) => {
    try {
      await updateTask(t.id, { status: t.status === "completed" ? "pending" : "completed" });
      toast.success(t.status === "completed" ? "Reaberta" : "Concluída");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTask(confirmDelete.id);
      toast.success("Tarefa excluída");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Tarefas
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            {isAdmin
              ? "Distribua tarefas para a equipe e acompanhe o andamento."
              : "Suas tarefas atribuídas. Marque como concluído quando finalizar."}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="add-task-button" className="bg-[#0F2942] hover:bg-[#173E63] text-white">
                <Plus className="h-4 w-4 mr-2" />
                Nova Tarefa
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editing ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
                <DialogDescription>Atribua a um funcionário e defina prioridade.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Título *</Label>
                  <Input data-testid="task-title-input" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: Enviar declaração mensal" className="mt-1" />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea data-testid="task-description-input" value={form.description} rows={3}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Detalhes da tarefa..." className="mt-1" />
                </div>
                <div>
                  <Label>Responsável *</Label>
                  <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                    <SelectTrigger data-testid="task-assignee-select" className="mt-1">
                      <SelectValue placeholder="Selecione o funcionário" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} <span className="text-slate-400 text-xs ml-1">({u.role})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Prioridade</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="task-priority-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="low">Baixa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Prazo (opcional)</Label>
                    <Input type="date" data-testid="task-due-date-input" value={form.due_date}
                      onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="task-submit-button" className="bg-[#0F2942] hover:bg-[#173E63] text-white">
                    {editing ? "Salvar" : "Criar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2">
        {[
          { v: "pending", label: "Pendentes" },
          { v: "completed", label: "Concluídas" },
          { v: "all", label: "Todas" },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`tasks-filter-${f.v}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === f.v ? "bg-[#0F2942] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-tasks">
            <ListTodo className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {tasks.length === 0 ? "Nenhuma tarefa ainda." : "Nada para exibir com esse filtro."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="tasks-list">
            {filtered.map((t) => {
              const overdue = isOverdue(t);
              const pmeta = PRIORITY_META[t.priority] || PRIORITY_META.medium;
              return (
                <li key={t.id} className={`p-4 flex flex-col sm:flex-row gap-3 sm:items-center transition-colors hover:bg-slate-50 ${overdue ? "border-l-4 border-l-red-400" : ""}`}
                    data-testid={`task-row-${t.id}`}>
                  <button onClick={() => toggleComplete(t)} data-testid={`task-toggle-${t.id}`}
                    className={`mt-0.5 flex-shrink-0 transition-colors ${t.status === "completed" ? "text-green-600" : "text-slate-400 hover:text-[#0F2942]"}`}>
                    {t.status === "completed" ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${t.status === "completed" ? "line-through text-slate-400" : "text-[#0F172A]"}`}>{t.title}</p>
                    {t.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                      <Badge variant="outline" className={pmeta.cls}>
                        <Flame className="h-3 w-3 mr-1" />
                        {pmeta.label}
                      </Badge>
                      <span className="text-slate-500">
                        {t.assignee_name || "—"}
                      </span>
                      {t.due_date && (
                        <span className={overdue ? "text-red-600 font-semibold flex items-center gap-1" : "text-slate-500"}>
                          {overdue && <AlertTriangle className="h-3 w-3" />}
                          Prazo: {formatBRDate(t.due_date)}
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(t)}
                        data-testid={`delete-task-${t.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-task" className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
